import { useState } from 'react';
import { Button } from '../ui/button';
import { Field, FormError } from '../ui/field';
import { Modal } from '../ui/modal';
import { deviceLabel, type InputDevices } from '../../lib/media';
import type { AddSourceInput } from '../../hooks/use-local-sources';

/** The "none" option in the audio picker: a video-only source is legitimate. */
const NO_AUDIO = '';

export interface AddSourceDialogProps {
  open: boolean;
  devices: InputDevices;
  /** Devices already driving a source, so the picker can mark them. */
  usedVideoDeviceIds: string[];
  busy: boolean;
  error: string | null;
  onSubmit: (input: AddSourceInput) => void;
  onCancel: () => void;
}

export function AddSourceDialog({
  open,
  devices,
  usedVideoDeviceIds,
  busy,
  error,
  onSubmit,
  onCancel,
}: AddSourceDialogProps) {
  const [label, setLabel] = useState('');
  const [videoDeviceId, setVideoDeviceId] = useState('');
  const [audioDeviceId, setAudioDeviceId] = useState(NO_AUDIO);

  // The default is *derived*, not copied into state by an effect. The host has
  // usually not chosen yet when this first renders, and enumeration can still
  // be running - deriving means a camera that appears while the dialog is open
  // can become the default, and that no effect has to keep two values in step.
  // The parent mounts this component only while the dialog is open, so the
  // choice resets on its own each time it is reopened.
  const firstFree = devices.video.find((device) => !usedVideoDeviceIds.includes(device.deviceId));
  const selectedVideoId = videoDeviceId || firstFree?.deviceId || devices.video[0]?.deviceId || '';

  return (
    <Modal
      open={open}
      title="Add a source"
      onClose={busy ? () => undefined : onCancel}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!selectedVideoId}
            onClick={() =>
              onSubmit({
                label,
                videoDeviceId: selectedVideoId,
                audioDeviceId: audioDeviceId === NO_AUDIO ? null : audioDeviceId,
              })
            }
          >
            Add source
          </Button>
        </>
      }
    >
      <FormError>{error}</FormError>

      <Field label="Name">
        {(id) => (
          <input
            id={id}
            className="input"
            type="text"
            placeholder="Wide shot, Close-up, Capture card..."
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        )}
      </Field>

      <Field label="Video device">
        {(id) => (
          <select
            id={id}
            className="select"
            value={selectedVideoId}
            disabled={devices.video.length === 0}
            onChange={(event) => setVideoDeviceId(event.target.value)}
          >
            {devices.video.length === 0 ? <option value="">No camera found</option> : null}
            {devices.video.map((device, position) => (
              <option key={device.deviceId} value={device.deviceId}>
                {deviceLabel(device, 'Camera', position + 1)}
                {usedVideoDeviceIds.includes(device.deviceId) ? ' (already in use)' : ''}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field label="Audio device">
        {(id) => (
          <select
            id={id}
            className="select"
            value={audioDeviceId}
            onChange={(event) => setAudioDeviceId(event.target.value)}
          >
            <option value={NO_AUDIO}>None - video only</option>
            {devices.audio.map((device, position) => (
              <option key={device.deviceId} value={device.deviceId}>
                {deviceLabel(device, 'Microphone', position + 1)}
              </option>
            ))}
          </select>
        )}
      </Field>

      <p className="muted">
        A capture card appears here as an ordinary camera. The same device can drive two sources,
        but most cards refuse a second reader.
      </p>
    </Modal>
  );
}
